import Heading from '@/components/core/Heading/Heading';
import Jumbotron from '@/components/core/Jumbotron/Jumbotron';
import Section from '@/components/core/Section/Section';
import SectionColumn from '@/components/core/Section/SectionColumn/SectionColumn';
import SectionItem from '@/components/core/Section/SectionItem/SectionItem';
import FeaturedItem from '@/components/features/visit/FeaturedItem/FeaturedItem';
import { Layout } from '@/components/shared/layouts/Layout/Layout';
import { ColorTypeOptions } from '@/components/shared/navigations/CoreNavigation/CoreNavigation';
import { getSafeLocale } from '@/utils/locale';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import styles from '../../../styles/museum-details.module.scss';

type MuseumDetailsProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const MuseumDetails: NextPage<MuseumDetailsProps> = () => {
	const { t } = useTranslation();

	return (
		<>
			<NextSeo title={t('chiang-mai-thailand')} />
			<Layout colorTypeActive={ColorTypeOptions.PRIMARY}>
				<Jumbotron
					backgroundImage={'/images/museum-details/header_th.jpg'}
					contentClassName={styles.museumDetailsJumbotron}
					color={Jumbotron.color.PRIMARY}
				>
					<div className={styles.header}>
						<div className={styles.headerLeft}>
							<div className={styles.headerLeftTitle}>
								<Link href={'/visit'} className={styles.link}>
									<span>{t('visit')}</span>
									&#x2192; <span>{t('chiang-mai-thailand')}</span>
								</Link>
							</div>
							<p>{t('thaimuseum.location_description')}</p>
						</div>
						<div className={styles.headerRight}>
							<div className={styles.headerRightTitle}></div>
							<p>
								{t('thaimuseum.address')}
								<br />
								<br />
								<Trans i18nKey="thaimuseum.workingHoursFull" />
								<br />
								<br />
								<Trans i18nKey="thaimuseum.workingHoursClosed" />
								<br />
							</p>
							<p>
								{t('thaimuseum.email')}
								<br />
								{t('thaimuseum.phone')}
							</p>
						</div>
					</div>
				</Jumbotron>
				<Section>
					<Heading text={t('museum-of-broken-relationships-th')} />
					<SectionColumn showTitle={false}>
						<FeaturedItem
							color={FeaturedItem.color.HIDDEN}
							align={FeaturedItem.align.LEFT}
							image={['/images/museum-details/museum.png', '/images/museum-details/museum@2x.png']}
							tilt={false}
						/>
					</SectionColumn>
					<SectionColumn showTitle={false}>
						<FeaturedItem
							color={FeaturedItem.color.PRIMARY}
							align={FeaturedItem.align.RIGHT}
							location={FeaturedItem.location.BOTTOM_LEFT}
							image={['/images/museum-details/staticmap-th.png']}
						>
							<p className={styles.mapTitle}>{t('thaimuseum.address')}</p>
							<a
								target="_blank"
								rel="noopener"
								href="https://maps.app.goo.gl/MivD8AjQiV1ApXEx7"
								className={styles.mapsLink}
							>
								{t('open-in-google-maps')}
							</a>
						</FeaturedItem>
					</SectionColumn>
				</Section>
				<Section color="Primary">
					<SectionColumn title={t('hours--admission')}>
						<SectionItem title={t('working-hours')} icon="/assets/icons/clock-primary.svg">
							<p>
								<Trans i18nKey="thaimuseum.workingHoursFull" />
							</p>
							<p>
								<Trans i18nKey="thaimuseum.workingHoursClosed" />
							</p>
						</SectionItem>

						<SectionItem title={t('display-language')} icon="/assets/icons/globe-primary.svg">
							<p>{t('thaimuseum.captions')}</p>
						</SectionItem>
						<SectionItem title={t('admissions')} icon="/assets/icons/ticket-primary.svg">
							<p>{t('thaimuseum.adults-price')}</p>
							<p>{t('thaimuseum.pupils-students-fulltime-wi')}</p>
							<p>{t('thaimuseum.groups-of-15-persons-or-more')}</p>
							<p>
								{`${t('groups-of-15-people-or-more-mu')} `}
								<a href={`mailto:${t('thaimuseum.email')}`}>{t('thaimuseum.email')}</a>.
							</p>
						</SectionItem>
					</SectionColumn>
					<SectionColumn title={t('getting-there')}>
						<SectionItem title={t('by-air')} icon="/assets/icons/by-air-primary.svg">
							<p>
								<a target="_blank" rel="noopener" href="https://chiangmai.airportthai.co.th/">
									{t('chiang-mai-airport') + ' '}
								</a>
								{t('chiang-mai-airport-distance')}
							</p>
						</SectionItem>
						<SectionItem title={t('by-car')} icon="/assets/icons/by-car-primary.svg">
							<p>{t('chiang-mai-airport-by-car')}</p>
						</SectionItem>
						<SectionItem title={t('by-public-transport')} icon={'/assets/icons/by-bus-primary.svg'}>
							<p>
								<Trans i18nKey="chiang-mai-airport-by-public-transport" />
							</p>
						</SectionItem>
					</SectionColumn>
				</Section>
			</Layout>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
	return {
		props: {
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default MuseumDetails;
